import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from '@mui/lab';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { PeopleService } from '../../../Component/Api/People/peopleService';
import AppAccordion from '../../../Component/Common/Components/Accordion/AppAccordion';
import AppCheckbox from '../../../Component/Common/Components/FormValidationInputs/AppCheckbox';
import { AppDatepicker } from '../../../Component/Common/Components/FormValidationInputs/AppDatepicker';
import AppSelectList, { IDropDownOptions } from '../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import AppTextInput from '../../../Component/Common/Components/FormValidationInputs/AppTextInput';
import { AllOccupationalLevelList, EmployeeLevelList } from "../../../Component/Common/Constants/Lists/DropdownListOptions";
import { SkeletonLoading } from '../../../Component/Common/SkeletonLoading/SkeletonLoading';
import { PeopleDtos } from '../../../Component/Model/Dto/People';
import { IBulkActions } from '../../../Component/Model/Table/Table';
import { offsetTimezone } from "../../../Component/Model/helpers";
import { CustomDialogTitle } from "../../Common/Custom/CustomDialogTitle";
import { PeopleScorecardLinks } from "../PeopleScorecardLinks/PeopleScorecardLinks";
import { personCreateSchema, personEditSchema } from './PersonValidation';
import PositionModal from './PositionsData/PositionModal';
import { PositionsDataTable } from './PositionsData/PositionsDatatable';
import { PositionsDataTableMapper } from './PositionsData/PositionsTableColumns';



interface IPeopleModalProps {
  id: number;
  showCreatePeopleModal: boolean;
  closeModal: () => void;
}


const genderList: IDropDownOptions[] = [
  { id: 1, value: 'Male' },
  { id: 2, value: 'Female' }
]

const raceList: IDropDownOptions[] = [
  { value: 'African', id: 1 },
  { value: 'Coloured', id: 2 },
  { value: 'Indian', id: 3 },
  { value: 'White', id: 4 },
  { value: 'Non South African', id: 5 }
]


const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  pt: 2,
  px: 4,
  pb: 3,
};

export const PeopleModal = (props: IPeopleModalProps) => {
  const { control, reset, handleSubmit, formState: { isSubmitting }, setValue, getValues } = useForm({
    mode: 'all',
    resolver: props.id === 0 ? yupResolver(personCreateSchema) : yupResolver(personEditSchema)
  });

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [employmentData, setEmploymentData] = useState<PeopleDtos.GetPersonEmploymentRequest[]>([]);
  const [employeeId, setEmployeeId] = useState<number>(0);
  const [personId, setPersonId] = useState<number>(props.id);
  const [counter, setCounter] = useState<number>(0);
  const [showPositionModal, setShowPositionModal] = useState<boolean>(false);


  const GetPersonData = async (id: number) => {
    if (id === 0) return;
    try {
      setIsLoading(true);
      const result = await PeopleService.GetPeople(id);
      reset(result.data);
      const getEmploymentData = await PeopleService.GetPersonEmployment(id, true);
      setCounter(counter + 1);
      setEmploymentData(getEmploymentData.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }

  }

  const handleDataSubmit = async (data: FieldValues) => {
    if (personId === 0) {
      try {
        const person = data as PeopleDtos.CreatePerson;
        if (person.dateOfBirth != null) {
          person.dateOfBirth = offsetTimezone(new Date(person.dateOfBirth.toDateString()));
        }
        const result = await PeopleService.CreatePerson(person);
        toast.success(`Person ${result.data.firstName} ${result.data.lastName} created successfully`);
        setPersonId(result.data.id);
        loadEmploymentData(result.data.id);
        setValue("id", result.data.id);

      } catch (error) {
        toast.error("Person could not be created or already exists")
        console.log(error)
      }
    } else {
      try {
        const person = data as PeopleDtos.UpdatePerson;
        if (person.dateOfBirth != null) {
          person.dateOfBirth = offsetTimezone(new Date(person.dateOfBirth.toDateString()));
        }
        const result = await PeopleService.UpdatePerson(person);
        toast.success(`Person ${result.data.firstName} ${result.data.lastName} updated successfully`);
        loadEmploymentData(personId);

      } catch (error) {
        console.log(error)
      }
    }

  }

  const loadEmploymentData = async (personId: number) => {
    setIsLoading(true);
    try {
      const result = await PeopleService.GetPersonEmployment(personId, true);
      setEmploymentData(result.data);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  }

  const onAddPosition = () => {
    setShowPositionModal(true);
  }

  const closePositionModal = () => {
    setEmployeeId(0);
    loadEmploymentData(personId);
    setShowPositionModal(false);
  }

  useEffect(() => {
    if (personId !== 0) {
      GetPersonData(personId);
    }
  }, []);

  const onEditPeopleRow = (personId: number, id: number) => {
    setEmployeeId(id);
    setPersonId(personId);
    setShowPositionModal(true);
  }

  const bulkActions: IBulkActions[] = [
    {
      label: 'Capture Position Data',
      action: onAddPosition
    }
  ]

  const postionsTableData = PositionsDataTableMapper(employmentData, onEditPeopleRow);

  return (
    <Box>
      <Dialog open={props.showCreatePeopleModal} onClose={() => { props.closeModal() }} aria-labelledby="form-dialog-title" maxWidth='lg' disableEnforceFocus>
        <CustomDialogTitle id="customized-dialog-title" onClose={() => props.closeModal()}>
          {personId === 0 ? 'Capture Person' : 'Edit Person'}
        </CustomDialogTitle>
        <DialogContent>
          {isLoading && <SkeletonLoading formLoading FieldAmount={6} />}
          {!isLoading &&
            <>
              <AppAccordion expandOnDefault={true} label={personId === 0 ? 'New Person' : 'Edit Person'}>
                <form id="PersonEditForm" onSubmit={handleSubmit(handleDataSubmit)}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={12}>
                      <AppTextInput control={control} name="identificationNumber" label="ID Number" />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppTextInput control={control} name="employeeCode" label="Employee Code" />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppTextInput control={control} name="firstName" label="Name" />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppTextInput control={control} name="lastName" label="Surname" />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppSelectList control={control} name="genderId" label="Gender" items={genderList} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppSelectList control={control} name="raceId" label="Race" items={raceList} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppDatepicker control={control} name="dateOfBirth" label="Date Of Birth" inputFormat='yyyy/MM/dd' minDate={new Date(1900, 1, 1)} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <AppCheckbox control={control} name='isDisabled' label='Disabled' disabled={false} />
                    </Grid>
                    {personId === 0 &&
                      <>
                        <Grid item xs={12} sm={12}>
                          <AppSelectList control={control} name='occupationLevelTypeId' label='Occupational Level' items={AllOccupationalLevelList} />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                          <AppSelectList control={control} name='employeeTypeId' label='Employment Type' items={EmployeeLevelList} />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                          <AppTextInput control={control} name='position' label='Position' />
                        </Grid>
                      </>
                    }
                  </Grid>
                </form>
              </AppAccordion>
              {personId !== 0 &&
                <>
                  <AppAccordion expandOnDefault={false} label='Position Data'>
                    <PositionsDataTable data={postionsTableData} isLoading={isLoading} counter={counter} bulkActions={bulkActions} />
                  </AppAccordion>
                  <AppAccordion expandOnDefault={false} label='Scorecard Links'>
                    <PeopleScorecardLinks personId={personId} counter={counter} />
                  </AppAccordion>
                </>
              }
            </>
          }
          {showPositionModal && <PositionModal employeeId={employeeId} personId={personId} displayPositionModal={showPositionModal} closePositionModal={closePositionModal} />}
        </DialogContent>
        <DialogActions>
          <LoadingButton loading={isSubmitting} variant='contained' form='PersonEditForm' type="submit" color='primary'>Save</LoadingButton>
        </DialogActions>
      </Dialog>

    </Box >
  )
}

